/**
 * Venta motion runtime.
 * Loads Lenis (smooth scroll), wires scroll-reveal, registers <venta-magnetic>.
 *
 * Block authors expose motion params via data-* attributes:
 *   <div data-reveal data-reveal-from="up" data-reveal-distance="48" data-reveal-delay="100">
 *   <venta-magnetic data-strength="0.3">...</venta-magnetic>
 *
 * Theme Editor users adjust these via block schema settings. No code edits needed.
 */

(() => {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // -------- Lenis smooth scroll (lazy-load only when needed) --------
  async function initLenis() {
    if (prefersReduced) return;
    if (window.__ventaLenis) return;
    try {
      const mod = await import(window.Shopify?.designMode
        ? '{{ "lenis.min.js" | asset_url }}'
        : new URL('./lenis.min.js', document.currentScript?.src || location.href).href);
      const Lenis = mod.default || window.Lenis;
      if (!Lenis) {
        // Loaded as global from theme.liquid script tag
      }
      const LenisCtor = window.Lenis || Lenis;
      const lenis = new LenisCtor({
        duration: 1.1,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        smoothTouch: false,
      });
      window.__ventaLenis = lenis;
      const raf = (time) => {
        lenis.raf(time);
        requestAnimationFrame(raf);
      };
      requestAnimationFrame(raf);
    } catch (e) {
      console.warn('[venta] Lenis init failed', e);
    }
  }

  // Simpler init: Lenis is loaded as a UMD/IIFE script tag from theme.liquid
  // and exposes window.Lenis. So we just construct it directly.
  function initLenisDirect() {
    if (prefersReduced) return;
    if (!window.Lenis) {
      // Wait one frame in case the script hasn't executed yet
      return requestAnimationFrame(initLenisDirect);
    }
    if (window.__ventaLenis) return;
    const lenis = new window.Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
    });
    window.__ventaLenis = lenis;
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
  }

  // -------- Scroll-reveal --------
  function initScrollReveal() {
    if (prefersReduced) {
      document.querySelectorAll('[data-reveal]').forEach((el) => {
        el.setAttribute('data-reveal-state', 'visible');
      });
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const delay = parseInt(el.dataset.revealDelay || '0', 10);
          const distance = el.dataset.revealDistance;
          if (distance) el.style.setProperty('--venta-reveal-distance', distance + 'px');
          if (delay) el.style.setProperty('--venta-reveal-delay', delay + 'ms');
          el.setAttribute('data-reveal-state', 'visible');
          io.unobserve(el);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );

    const scan = () => {
      document.querySelectorAll('[data-reveal]:not([data-reveal-state])').forEach((el) => {
        io.observe(el);
      });
    };
    scan();

    // Re-scan when Shopify section editor mutates DOM
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:load', scan);
      document.addEventListener('shopify:block:select', scan);
    }
  }

  // -------- Animated counter web component --------
  // <venta-counter data-end="99.9" data-decimals="1" data-suffix="%" data-duration="1200" data-from="0" data-countdown="false">99.9%</venta-counter>
  class VentaCounter extends HTMLElement {
    connectedCallback() {
      if (prefersReduced) return;
      this._end = parseFloat(this.dataset.end);
      this._decimals = parseInt(this.dataset.decimals || '0', 10);
      this._suffix = this.dataset.suffix || '';
      this._duration = parseInt(this.dataset.duration || '1200', 10);
      this._from = parseFloat(this.dataset.from || '0');
      this._countdown = this.dataset.countdown === 'true';
      this._finalText = `${this._end.toFixed(this._decimals)}${this._suffix}`;
      // Reserve space without revealing the final number until animation
      this.textContent = this._finalText;
      this.style.visibility = 'hidden';
      this._io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            this._io.disconnect();
            this._animate();
            return;
          }
        }
      }, { threshold: 0.3 });
      this._io.observe(this);
    }
    disconnectedCallback() {
      this._io?.disconnect();
    }
    _animate() {
      this.style.visibility = 'visible';
      const start = performance.now();
      const startVal = this._countdown ? this._from : 0;
      const endVal = this._end;
      const range = endVal - startVal;
      const tick = (now) => {
        const t = Math.min(1, (now - start) / this._duration);
        const eased = 1 - Math.pow(1 - t, 3); // cubic-out
        const val = startVal + range * eased;
        this.textContent = `${val.toFixed(this._decimals)}${this._suffix}`;
        if (t < 1) requestAnimationFrame(tick);
        else this.textContent = this._finalText;
      };
      requestAnimationFrame(tick);
    }
  }
  if (!customElements.get('venta-counter')) {
    customElements.define('venta-counter', VentaCounter);
  }

  // -------- Magnetic button web component --------
  class VentaMagnetic extends HTMLElement {
    connectedCallback() {
      if (prefersReduced) return;
      this.strength = parseFloat(this.dataset.strength || '0.3');
      this._onMove = this._onMove.bind(this);
      this._onLeave = this._onLeave.bind(this);
      this.addEventListener('pointermove', this._onMove);
      this.addEventListener('pointerleave', this._onLeave);
    }
    disconnectedCallback() {
      this.removeEventListener('pointermove', this._onMove);
      this.removeEventListener('pointerleave', this._onLeave);
    }
    _onMove(e) {
      const r = this.getBoundingClientRect();
      const x = (e.clientX - (r.left + r.width / 2)) * this.strength;
      const y = (e.clientY - (r.top + r.height / 2)) * this.strength;
      this.style.transform = `translate(${x}px, ${y}px)`;
    }
    _onLeave() {
      this.style.transform = '';
    }
  }
  if (!customElements.get('venta-magnetic')) {
    customElements.define('venta-magnetic', VentaMagnetic);
  }

  // -------- Mobile header reorder (move drawer toggle to right column) --------
  // Match Lovable's layout: Logo LEFT, Actions + Hamburger RIGHT.
  // Horizon's default places drawer in the left column with the logo. We move
  // it into the right column so the visual order reads correctly on mobile.
  function reorderMobileHeader() {
    const mq = window.matchMedia('(max-width: 749px)');
    const apply = () => {
      const drawer = document.querySelector('#header-component .header__column--left .header__drawer');
      const right = document.querySelector('#header-component .header__column--right');
      if (!drawer || !right) return;
      if (mq.matches) {
        if (drawer.parentElement !== right) {
          drawer.dataset.ventaMoved = 'true';
          right.appendChild(drawer);
        }
      } else {
        // restore to left column on desktop
        if (drawer.dataset.ventaMoved === 'true') {
          const left = document.querySelector('#header-component .header__column--left');
          if (left) left.insertBefore(drawer, left.firstChild);
          delete drawer.dataset.ventaMoved;
        }
      }
    };
    apply();
    mq.addEventListener?.('change', apply);
    // Re-apply if Shopify section editor re-renders the header
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:load', apply);
    }
  }

  // -------- Init on DOM ready --------
  const start = () => {
    initLenisDirect();
    initScrollReveal();
    reorderMobileHeader();
    initVentaCounters();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // -------- Venta slideshow counter (Lovable 01 [bar] 06 indicator) --------
  // Driven by the `counter` style in snippets/slideshow-controls.liquid. Zero-pads
  // the current/total numbers and updates the bar fill on slide change.
  function initVentaCounters() {
    const counters = document.querySelectorAll('[data-venta-counter]:not([data-venta-wired])');
    counters.forEach((counter) => {
      counter.setAttribute('data-venta-wired', '1');
      const total = parseInt(counter.getAttribute('data-total'), 10) || 1;
      const currentEl = counter.querySelector('[ref="current"]');
      const totalEl = counter.querySelector('.venta-counter__num--total');
      const fillEl = counter.querySelector('[data-venta-counter-fill]');
      const pad = (n) => String(n).padStart(2, '0');
      const sync = () => {
        if (!currentEl) return;
        const raw = parseInt((currentEl.textContent || '1').trim(), 10) || 1;
        const padded = pad(raw);
        if (currentEl.textContent !== padded) currentEl.textContent = padded;
        if (fillEl) fillEl.style.width = (raw / total * 100) + '%';
      };
      if (totalEl) totalEl.textContent = pad(total);
      sync();
      if (currentEl && typeof MutationObserver !== 'undefined') {
        new MutationObserver(sync).observe(currentEl, {
          childList: true,
          characterData: true,
          subtree: true,
        });
      }
    });
  }

  // Re-init on Theme Editor section reloads.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', initVentaCounters);
  }
})();

/* ============================================================================
 * <venta-device-selector>
 *
 * Renders Clear/Privacy tabs and an iPhone catalog grid built from Shopify
 * variants. Models the product doesn't carry render as Sold Out with line-through
 * so the grid is always visually complete. Selection fires `venta:select` on the
 * element. Consumers can also opt into declarative wiring via
 * data-venta-ds-listen="<instance_id>" with [data-venta-target="..."] inside.
 * ==========================================================================*/
(() => {
  const CATALOG = [
    { displayTitle: 'iPhone 18 Pro Max', generation: '18', screenSize: '6.9' },
    { displayTitle: 'iPhone 18 Pro',     generation: '18', screenSize: '6.3' },
    { displayTitle: 'iPhone 17 Pro Max', generation: '17', screenSize: '6.9' },
    { displayTitle: 'iPhone 17 Pro',     generation: '17', screenSize: '6.3' },
    { displayTitle: 'iPhone 17 Plus',    generation: '17', screenSize: '6.7' },
    { displayTitle: 'iPhone 17 Air',     generation: '17', screenSize: '6.55' },
    { displayTitle: 'iPhone 17',         generation: '17', screenSize: '6.3' },
    { displayTitle: 'iPhone 16 Pro Max', generation: '16', screenSize: '6.9' },
    { displayTitle: 'iPhone 16 Pro',     generation: '16', screenSize: '6.3' },
    { displayTitle: 'iPhone 16 Plus',    generation: '16', screenSize: '6.7' },
    { displayTitle: 'iPhone 16e',        generation: '16', screenSize: '6.1' },
    { displayTitle: 'iPhone 16',         generation: '16', screenSize: '6.1' },
    { displayTitle: 'iPhone 15 Pro Max', generation: '15', screenSize: '6.7' },
    { displayTitle: 'iPhone 15 Pro',     generation: '15', screenSize: '6.1' },
    { displayTitle: 'iPhone 15 Plus',    generation: '15', screenSize: '6.7' },
    { displayTitle: 'iPhone 15',         generation: '15', screenSize: '6.1' },
    { displayTitle: 'iPhone 14 Pro Max', generation: '14', screenSize: '6.7' },
    { displayTitle: 'iPhone 14 Pro',     generation: '14', screenSize: '6.1' },
    { displayTitle: 'iPhone 14 Plus',    generation: '14', screenSize: '6.7' },
    { displayTitle: 'iPhone 14',         generation: '14', screenSize: '6.1' },
    { displayTitle: 'iPhone 13 Pro Max', generation: '13', screenSize: '6.7' },
    { displayTitle: 'iPhone 13 Pro',     generation: '13', screenSize: '6.1' },
    { displayTitle: 'iPhone 13 Mini',    generation: '13', screenSize: '5.4' },
    { displayTitle: 'iPhone 13',         generation: '13', screenSize: '6.1' },
  ];

  // "iPhone 16 Pro / 17 Pro / 17" -> ["iPhone 16 Pro", "iPhone 17 Pro", "iPhone 17"]
  function expandTitle(title) {
    const parts = String(title).split(/\s*\/\s*/);
    if (parts.length <= 1) return [parts[0].trim()];
    const prefixMatch = parts[0].trim().match(/^(iPhone\s*)/i);
    const prefix = prefixMatch ? prefixMatch[1] : '';
    return parts.map((p, i) => {
      const t = p.trim();
      if (i === 0) return t;
      if (/^iPhone/i.test(t)) return t;
      return `${prefix}${t}`;
    });
  }

  function buildCatalogWithAvailability(variants) {
    const map = new Map();
    for (const v of variants) {
      for (const t of expandTitle(v.title)) {
        if (!map.has(t.toLowerCase())) map.set(t.toLowerCase(), v);
      }
    }
    const groups = [];
    let cur = '';
    for (const m of CATALOG) {
      const match = map.get(m.displayTitle.toLowerCase());
      const entry = {
        ...m,
        available: !!match && match.available,
        variantId: match ? match.id : null,
        price: match ? match.price : null,
        compareAt: match ? match.compareAt : null,
        // Per-variant delivery note (metafield custom.delivery_note). Shown
        // under the grid while this model is selected; empty = nothing shown.
        note: match && match.note ? String(match.note) : '',
      };
      if (m.generation !== cur) {
        cur = m.generation;
        groups.push({ generation: cur, models: [entry] });
      } else {
        groups[groups.length - 1].models.push(entry);
      }
    }
    return groups;
  }

  function formatPrice(cents) {
    if (cents == null) return '';
    return String(Math.round(cents / 100));
  }

  function escapeAttr(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function shortLabel(displayTitle) {
    const stripped = displayTitle.replace(/^iPhone\s*/i, '');
    return /^\d+$/.test(stripped) ? `iPhone ${stripped}` : stripped;
  }

  class VentaDeviceSelector extends HTMLElement {
    constructor() {
      super();
      this.currentTab = 'clear';
      this.selected = { clear: null, privacy: null };
    }

    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      // Brand-aware host: the snippet sets data-default-tab="privacy" on the
      // Privacy PDP so the right pill + models show on arrival.
      this.currentTab = this.dataset.defaultTab || 'clear';

      this.clearVariants = this._parseVariants('clear');
      this.privacyVariants = this._parseVariants('privacy');

      if (this.clearVariants) this._renderGrid('clear', this.clearVariants);
      if (this.privacyVariants) this._renderGrid('privacy', this.privacyVariants);

      this._bindTabs();
      this._bindGridDelegation();
      this._announce({ brand: this.currentTab, variantId: null, price: null, displayTitle: null });
    }

    _parseVariants(brand) {
      const script = this.querySelector(`[data-venta-ds-${brand}-variants]`);
      if (!script) return null;
      try { return JSON.parse(script.textContent); } catch { return null; }
    }

    _renderGrid(brand, variants) {
      const host = this.querySelector(`[data-venta-ds-grid="${brand}"]`);
      if (!host) return;
      const groups = buildCatalogWithAvailability(variants || []);
      // Series-pill row: inert in default mode (CSS-hidden); the host-level
      // venta-ds--series class switches the selector to one-series-at-a-time.
      const seriesRow = `
        <div class="venta-ds__series" role="tablist">
          ${groups.map((g, i) => `
            <button type="button" class="venta-ds__series-pill${i === 0 ? ' venta-ds__series-pill--active' : ''}" data-series="${g.generation}">${g.generation}</button>
          `).join('')}
        </div>
      `;
      host.innerHTML = seriesRow + groups.map((g, i) => `
        <div class="venta-ds__group${i === 0 ? ' venta-ds__group--active' : ''}" data-series-panel="${g.generation}">
          <p class="venta-ds__group-label">iPhone ${g.generation}</p>
          <div class="venta-ds__grid">
            ${g.models.map((m) => {
              const cls = m.available ? 'venta-ds__model--available' : 'venta-ds__model--sold-out';
              const label = shortLabel(m.displayTitle);
              const suffix = m.available
                ? `<span class="venta-ds__model-screen">${m.screenSize}<span class="venta-ds__inch">"</span></span>`
                : `<span class="venta-ds__model-sold-out">Sold Out</span>`;
              return `
                <button type="button" class="venta-ds__model ${cls}"
                  data-variant-id="${m.variantId ?? ''}"
                  data-price="${m.price ?? ''}"
                  data-compare="${m.compareAt ?? ''}"
                  data-display="${m.displayTitle}"
                  data-note="${escapeAttr(m.note)}"
                  ${m.available ? '' : 'disabled aria-disabled="true"'}>
                  <span class="venta-ds__model-name">${label}</span>
                  ${suffix}
                </button>
              `;
            }).join('')}
          </div>
        </div>
      `).join('');
    }

    _bindTabs() {
      this.querySelectorAll('[data-venta-ds-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this._switchTab(tab.dataset.ventaDsTab));
      });
    }

    _bindGridDelegation() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-venta-ds-grid] button');
        if (!btn || btn.disabled) return;
        if (btn.dataset.series) {
          // series pill (series mode only): swap the visible generation
          const host = btn.closest('[data-venta-ds-grid]');
          host.querySelectorAll('.venta-ds__series-pill').forEach((p) => {
            p.classList.toggle('venta-ds__series-pill--active', p === btn);
          });
          host.querySelectorAll('[data-series-panel]').forEach((p) => {
            p.classList.toggle('venta-ds__group--active', p.dataset.seriesPanel === btn.dataset.series);
          });
          return;
        }
        const brand = btn.closest('[data-venta-ds-grid]').dataset.ventaDsGrid;
        this._selectModel(brand, btn);
      });
    }

    _switchTab(brand) {
      if (this.currentTab === brand) return;
      this.currentTab = brand;
      this.querySelectorAll('[data-venta-ds-tab]').forEach((t) => {
        const active = t.dataset.ventaDsTab === brand;
        t.classList.toggle('venta-ds__tab--active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.querySelectorAll('[data-venta-ds-panel]').forEach((p) => {
        p.hidden = p.dataset.ventaDsPanel !== brand;
      });
      const sel = this.selected[brand];
      this._announce(sel
        ? { brand, ...sel }
        : { brand, variantId: null, price: null, displayTitle: null });
    }

    _selectModel(brand, btn) {
      this.querySelectorAll(`[data-venta-ds-grid="${brand}"] button`).forEach((b) => {
        b.classList.remove('venta-ds__model--selected');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('venta-ds__model--selected');
      btn.setAttribute('aria-pressed', 'true');
      const sel = {
        variantId: btn.dataset.variantId,
        price: btn.dataset.price ? parseInt(btn.dataset.price, 10) : null,
        compareAt: btn.dataset.compare ? parseInt(btn.dataset.compare, 10) : null,
        displayTitle: btn.dataset.display,
        note: btn.dataset.note || '',
      };
      this.selected[brand] = sel;
      this._announce({ brand, ...sel });
    }

    _announce(detail) {
      const productId = this.dataset[`${detail.brand}Id`];
      const productHandle = this.dataset[`${detail.brand}Handle`];
      const productUrl = this.dataset[`${detail.brand}Url`];
      const currency = this.dataset.currency || 'EGP';
      const evt = new CustomEvent('venta:select', {
        bubbles: true,
        detail: { ...detail, productId, productHandle, productUrl, currency },
      });
      this.dispatchEvent(evt);

      // Delivery note: lives inside the selector so PDP + Armory both get it.
      // First sentence = headline, the rest = detail line.
      const noteEl = this.querySelector('[data-venta-ds-note]');
      if (noteEl) {
        const showNote = !!detail.variantId && !!detail.note;
        const titleEl = noteEl.querySelector('[data-venta-ds-note-title]');
        const detailEl = noteEl.querySelector('[data-venta-ds-note-detail]');
        if (showNote) {
          const text = String(detail.note).trim();
          const cut = text.search(/\.\s+/);
          const title = (cut > -1 ? text.slice(0, cut) : text).replace(/\.$/, '');
          const rest = cut > -1 ? text.slice(cut + 1).trim() : '';
          if (titleEl) titleEl.textContent = title;
          if (detailEl) { detailEl.textContent = rest; detailEl.hidden = !rest; }
          if (noteEl.hidden || noteEl.dataset.note !== text) {
            noteEl.classList.remove('venta-ds__note--in');
            void noteEl.offsetWidth; // restart the fade-in on a new note
            noteEl.classList.add('venta-ds__note--in');
          }
          noteEl.dataset.note = text;
        }
        noteEl.hidden = !showNote;
      }

      // Declarative listeners: any element with data-venta-ds-listen="<id>"
      const listeners = document.querySelectorAll(`[data-venta-ds-listen="${this.id}"]`);
      listeners.forEach((listener) => {
        const idInput = listener.querySelector('[data-venta-target="variantId"]');
        const priceEl = listener.querySelector('[data-venta-target="price"]');
        const fromEl = listener.querySelector('[data-venta-target="from"]');
        const atcEl = listener.querySelector('[data-venta-target="atc"]');
        const urlEl = listener.querySelector('[data-venta-target="productUrl"]');
        const compareEl = listener.querySelector('[data-venta-target="compare"]');
        const saleEl = listener.querySelector('[data-venta-target="saleBadge"]');
        const onSale = detail.compareAt != null && detail.price != null && detail.compareAt > detail.price;
        if (idInput) idInput.value = detail.variantId || '';
        if (priceEl && detail.price != null) priceEl.textContent = formatPrice(detail.price);
        if (fromEl) fromEl.hidden = !!detail.variantId;
        if (atcEl) atcEl.disabled = !detail.variantId;
        if (urlEl && productUrl) urlEl.setAttribute('href', productUrl);
        if (compareEl) {
          if (detail.variantId && onSale) {
            compareEl.textContent = `${formatPrice(detail.compareAt)} ${currency}`;
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
        if (saleEl) {
          if (!detail.variantId) {
            // No model picked: fall back to the Liquid-rendered "Up to -X%".
            saleEl.textContent = saleEl.dataset.defaultLabel || saleEl.textContent;
            saleEl.hidden = false;
          } else if (onSale) {
            const pct = Math.round(((detail.compareAt - detail.price) / detail.compareAt) * 100);
            saleEl.textContent = `-${pct}%`;
            saleEl.hidden = false;
          } else {
            saleEl.hidden = true;
          }
        }
      });
    }
  }

  if (!customElements.get('venta-device-selector')) {
    customElements.define('venta-device-selector', VentaDeviceSelector);
  }
})();

/* ============================================================================
 * Venta manifesto scroll — word-by-word reveal (Lovable VentaStoryPage parity).
 * The element is a tall (400vh desktop / 170vh mobile) wrapper with a sticky
 * full-height inner. Scroll progress through the wrapper maps each word's
 * opacity from 0.04 to 1, in document order, across the configured range.
 * ==========================================================================*/
(() => {
  class VentaManifestoScroll extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      const textHost = this.querySelector('[data-mscroll-text]');
      if (!textHost) return;

      // Wrap every word in a span; <strong>/<b> ancestry marks bold words.
      const words = [];
      const paragraphs = Array.from(textHost.querySelectorAll('p'));
      paragraphs.forEach((p) => {
        const frag = document.createDocumentFragment();
        const walk = (node, bold) => {
          Array.from(node.childNodes).forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
              child.textContent.split(/\s+/).filter(Boolean).forEach((w) => {
                const span = document.createElement('span');
                span.className = 'venta-mscroll__word' + (bold ? ' venta-mscroll__word--bold' : '');
                span.textContent = w;
                frag.appendChild(span);
                words.push(span);
              });
            } else if (child.nodeType === Node.ELEMENT_NODE) {
              walk(child, bold || child.tagName === 'STRONG' || child.tagName === 'B');
            }
          });
        };
        walk(p, false);
        p.textContent = '';
        p.appendChild(frag);
      });

      if (!words.length) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        words.forEach((w) => { w.style.opacity = '1'; });
        return;
      }

      let ticking = false;
      const update = () => {
        ticking = false;
        const rect = this.getBoundingClientRect();
        const total = rect.height - window.innerHeight;
        const progress = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 1;
        const mobile = window.innerWidth < 750;
        const start = mobile ? 0.02 : 0.04;
        const end = mobile ? 0.42 : 0.65;
        const range = end - start;
        const n = words.length;
        for (let i = 0; i < n; i++) {
          const ws = start + (i / n) * range;
          const we = start + ((i + 1) / n) * range;
          const o = Math.min(1, Math.max(0, (progress - ws) / (we - ws)));
          words[i].style.opacity = (0.04 + o * 0.96).toFixed(3);
        }
      };
      const onScroll = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    }
  }

  if (!customElements.get('venta-manifesto-scroll')) {
    customElements.define('venta-manifesto-scroll', VentaManifestoScroll);
  }
})();
